import React from "react";
import { IconTextLink } from "component-library/src/components/Atoms/IconTextLink/IconTextLink";
import { useTranslation } from "i18n";
import { dataTestIds } from "data-testids";

import "./SelectAllControl.scss";

interface IItems {
  id: string;
}

export interface ISelectAllControlTexts {
  markAsReadAlertsLabel: string;
  markAsUnreadAlertsLabel: string;
  selectAllAlertsLabel: string;
}

export interface ISelectAllControlProps {
  selectedItems: string[];
  markItemsAsRead: (selectedItems: string[]) => void;
  markItemsAsUnread: (selectedItems: string[]) => void;
  deselectItems: (selectedItems: string[]) => void;
  selectItems: (selectedItems: string[]) => void;
  items: IItems[];
  onSelectAllClick?: () => void;
}

const SelectAllControl: React.FC<
  React.PropsWithChildren<ISelectAllControlProps>
> = (props) => {
  const { t } = useTranslation();
  const { selectedItems } = props;
  const items = props.items.map((item) => item.id);
  const isAllSelected =
    Boolean(items.length) &&
    items.every((item) => selectedItems.includes(item));

  const markItemsAsRead = () => {
    if (selectedItems.length > 0) {
      props.markItemsAsRead(selectedItems);
    }
  };

  const markItemsAsUnread = () => {
    if (selectedItems.length > 0) {
      props.markItemsAsUnread(selectedItems);
    }
  };

  const handleSelectAllToggle = (
    event: React.MouseEvent<HTMLAnchorElement>
  ) => {
    event.preventDefault();

    if (isAllSelected) {
      props.deselectItems(items);
    } else {
      props.selectItems(
        items.filter((item) => !props.selectedItems.includes(item))
      );
      props.onSelectAllClick?.();
    }
  };

  return (
    <div className="c-select-all-control">
      <IconTextLink
        variant="primary"
        renderLabel={
          <span className="c-icon-text-link__text">
            {isAllSelected
              ? t("common:button.label.deselect.all")
              : t("common:button.label.select.all")}
          </span>
        }
        handleClick={handleSelectAllToggle}
        className="mr-2 p-0"
      />
      {selectedItems.length > 0 && (
        <div className="flex items-center gap-1">
          <IconTextLink
            variant="primary"
            renderLabel={
              <span className="c-icon-text-link__text">
                {t("common:button.label.mark.as.read")}
              </span>
            }
            handleClick={markItemsAsRead}
            data-testid={
              dataTestIds.nuJobsApp["Components.SelectAllControls.markAsRead"]
            }
            className="p-0"
          />
          |
          <IconTextLink
            variant="primary"
            renderLabel={
              <span className="c-icon-text-link__text">
                {t("common:button.label.mark.as.unread")}
              </span>
            }
            handleClick={markItemsAsUnread}
            data-testid={
              dataTestIds.nuJobsApp["Components.SelectAllControls.markAsUnread"]
            }
            className="p-0"
          />
        </div>
      )}
    </div>
  );
};

export default SelectAllControl;
